import { _ } from "../../core/polyglot/polyglot.js";
import {
	copyIcon,
	editIcon,
	fileUploadIcon,
	pencilSolidIcon,
	plusIcon,
	syncIcon,
	trashSolidAltIcon,
} from "../../core/elements/icons/index.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import { notBlank, validateAll } from "../../core/widgets/forms/validators.js";
import { router } from "@foretagsplatsen/widgetjs";
import accountingDocument from "../shared/accountingDocument.js";
import Budget from "../../models/Budget.js";
import BudgetImporter from "./BudgetImporter.js";
import budgetListWidget from "./budgetListWidget.js";
import EmptyArchiveWidget from "../../core/widgets/emptyArchive/EmptyArchiveWidget.js";
import FileUploadButtonWidget from "../../core/widgets/FileUploadButtonWidget.js";
import FiscalYear from "../../models/period/FiscalYear.js";
import FiscalYearPickerWidget from "../../core/widgets/fiscalYear/FiscalYearPickerWidget.js";
import notifier from "../../core/widgets/notifier/notifier.js";
import session from "../../core/session.js";
import SwitchButton from "../../core/widgets/SwitchButton/SwitchButton.js";
import UploadService from "../../core/services/UploadService.js";

/**
 * Render the list of all budgets belonging to a company.
 *
 * It also lets the user select a budget and provides actions to add/edit/delete
 * budgets.
 */
const budgetArchiveDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		that.documentId = "budget-archive";

		that.title = _("Budgets");

		that.help = () => [
			{
				title: _("Budget"),
				content: _(
					"This is an archive for your budgets, both the ones you have imported with the accounting data and the ones you or your colleagues have created in here.",
				),
			},
			{
				title: _("Add budget"),
				content: _(
					"By clicking this button you will begin the creation of a new budget. You can start from a blank page or fetch the data from a previous fiscal year and start from that.",
				),
				element: "add-budget",
			},
			{
				title: _("Import budget from SIE"),
				content: _(
					"At the moment we support uploading budget values using the SIE-format. We support imports of SIE2, 3 and 4 files. Please note that we will only import budget values from this import page, i.e we will ignore transactions and opening balances when importing SIE4-files.",
				),
				element: "import-sie",
			},
			{
				title: _("Import budget from SIE"),
				content: _(
					"For all import of budget values using a direct integration or other file format than SIE, e.g SAF-T, please use the regular import in company settings.",
				),
				element: "import-sie",
			},
		];

		my.availableFiscalYears = session
			.getCompany()
			.getYears()
			.sort((a, b) => (a.endsBefore(b) ? 1 : -1));

		my.nextFiscalYear = my.availableFiscalYears[0].forecastFollowingYear();

		my.fiscalYearRange = {
			startDate: my.nextFiscalYear.getStart(),
			endDate: my.nextFiscalYear.getEnd(),
		};

		my.availableFiscalYears.unshift(my.nextFiscalYear);

		my.budgetImporter = new BudgetImporter();

		const antiForgeryToken = session.getAntiForgeryToken();

		my.budgetRepository = makeMonitorRepository({
			route: "Accounting2/Company/#companyId/Budget/?budgetId",
			model: Budget,
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
			},
			antiForgeryToken,
		});

		my.fiscalYearRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/FiscalYear/Forecast/#fiscalYearId",
			model: FiscalYear,
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
			},
			errorMessage: _("Failed to create fiscal year"),
			antiForgeryToken,
		});

		that.showEmpty();
	};

	my.initializeSubwidgets = function () {
		my.super();

		my.budgetListWidget = budgetListWidget();
		my.budgetListWidget.budgetSelected.register(() => my.updateActions());

		const uploadService = new UploadService({
			uploadPath: my.budgetImporter.getUploadUrl(),
		});

		my.budgetImportButton = new FileUploadButtonWidget({
			label: my.budgetUploadRenderer(),
			dataTestName: "import-sie",
			dataHelpName: "import-sie",
			cssClass: "btn btn-block btn-primary",
			onUpload: uploadService.uploadFile.bind(uploadService),
			onSuccess: (file) => my.createBudgetFromFile(file),
			onError: ({ error }) => my.onUploadError({ error }),
		});
	};

	that.actions = function () {
		if (!my.canEditBudget()) {
			return [];
		}
		return [
			{
				label: _("Budgets"),
				children: [my.renderAddBudgetOn, my.budgetImportButton],
			},
			my.renderSelectedBudgetActionsOn,
			my.renderVisibilitySectionOn,
		];
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	that.hasActionsMenu = function () {
		return true;
	};

	that.show = async function () {
		that.showUpdating();

		const budgets = await my.budgetRepository.get();
		my.budgetListWidget.setBudgets(budgets);

		if (my.isEmpty()) {
			that.showEmpty();
		} else {
			that.showContent();
		}
	};

	my.canEditBudget = function () {
		const currentUser = session.getCurrentUser();
		return currentUser.canEditBudgetFor(session.getCompany());
	};

	my.renderContentOn = function (html) {
		html.render(my.budgetListWidget);
	};

	my.renderEmptyContentOn = function (html) {
		let actions = () => {};

		if (my.canEditBudget()) {
			actions = html
				.button(
					{ klass: "btn" },
					plusIcon,
					_("Make your first budget in minutes"),
				)
				.click(my.showAddBudgetDialog);
		}

		html.render(
			new EmptyArchiveWidget({
				title: _("No budget - no plan"),
				actions,
			}),
		);
	};

	my.renderAddBudgetOn = function (html) {
		html.button(
			{
				klass: "btn btn-block btn-primary",
				click: my.showAddBudgetDialog,
				"data-help": "add-budget",
				"data-test": "add-budget",
			},
			plusIcon,
			_("Add budget"),
		);
	};

	my.renderSelectedBudgetActionsOn = function (html) {
		const selectedBudget = my.getSelectedBudget();
		if (!selectedBudget) {
			return;
		}

		html.render(
			my.rightMenuSection(_("Actions"), [
				html
					.a(
						{
							klass: "btn btn-block btn-default",
							"data-test": "edit",
							click: () => {},
						},
						editIcon,
						_("Edit"),
					)
					.click(() =>
						my.redirectToBudgetEditor(my.getSelectedBudget()),
					),
				html.button(
					{
						klass: "btn btn-block btn-default",
						"data-test": "save-as",
						click: () => my.showSaveBudgetAsDialog(selectedBudget),
					},
					copyIcon,
					_("Save as..."),
				),
				html.button(
					{
						klass: "btn btn-block btn-default",
						"data-test": "delete",
						click: () => my.showDeleteBudgetDialog(selectedBudget),
					},
					trashSolidAltIcon,
					_("Delete"),
				),
				html.button(
					{
						klass: "btn btn-block btn-default",
						"data-test": "rename",
						click: () => my.showRenameBudgetDialog(selectedBudget),
					},
					pencilSolidIcon,
					_("Rename"),
				),
				(html) => {
					if (selectedBudget.isCurrent()) {
						return;
					}

					html.button(
						{
							klass: "btn btn-block btn-default",
							"data-test": "set-as-default-budget",
							click: () => my.setBudgetAsCurrent(selectedBudget),
						},
						syncIcon,
						_("Set as default budget"),
					);
				},
			]),
		);
	};

	my.renderVisibilitySectionOn = function (html) {
		const selectedBudget = my.getSelectedBudget();
		if (!selectedBudget) {
			return;
		}

		const button = new SwitchButton({
			dataTestName: "visibility",
			state: selectedBudget.isVisibleToAllUsers(),
			onStateChanged: (state) => {
				selectedBudget.setVisibleToAllUsers(state);
				my.updateBudget(selectedBudget);
			},
		});

		html.render(
			my.rightMenuSection("", (html) => {
				html.div(
					{ klass: "right-menu-block" },
					html.span(
						{ klass: "switch-label" },
						_("Visible to client users"),
					),
					button,
				);
			}),
		);
	};

	my.budgetUploadRenderer = function () {
		return (html) => html.div(fileUploadIcon, _("Import from SIE"));
	};

	my.showAddBudgetDialog = function () {
		my.showBudgetFormDialog({
			budget: new Budget(),
			showFiscalYearDropdown: true,
			header: _("New budget"),
			callback: my.saveBudget,
		});
	};

	my.showImportBudgetDialog = function ({ file, uploadedFiscalYear }) {
		let budgetName = "";

		my.configure({
			title: _("Import from {fileName}", { fileName: file.name }),
			okLabel: _("Create budget"),
			formSpec: { saveAction: () => my.importBudget(budgetName) },
			formBuilder: (form) => {
				form.legend(uploadedFiscalYear.toRangeString());

				form.input({
					label: _("Budget name"),
					name: "budget",
					accessor: () => budgetName,
					mutator: (name) => (budgetName = name),
					validator: validateAll(notBlank(), my.budgetNameValidator),
				});
			},
		});
	};

	my.importBudget = async function (budgetName) {
		await my.budgetImporter.importBudget(budgetName);

		my.reloadPage();
	};

	my.budgetNameValidator = function (name) {
		const isBudgetNameUsed = my.budgetListWidget
			.getBudgets()
			.some((budget) => budget.getName() === name);

		if (isBudgetNameUsed) {
			throw _("Budget name is already used");
		}
	};

	my.showDeleteBudgetDialog = function (budget) {
		my.confirmDeletion({
			name: budget.getName(),
			callback: () => my.deleteBudget(budget),
		});
	};

	my.showRenameBudgetDialog = function (budget) {
		my.showBudgetFormDialog({
			budget,
			header: _("Rename budget"),
			callback: my.updateBudget,
		});
	};

	my.showSaveBudgetAsDialog = function (budget) {
		my.showBudgetFormDialog({
			budget: budget.copy(),
			header: _("Save budget as…"),
			callback: my.saveBudget,
		});
	};

	my.showBudgetFormDialog = function ({
		budget,
		header,
		showFiscalYearDropdown = false,
		callback,
	}) {
		my.configure({
			title: header,
			formSpec: {
				saveAction: callback,
				model: budget,
			},
			formBuilder: (form) => {
				form.input({
					label: _("Budget name"),
					accessor: () => budget.getName(),
					mutator: (name) => budget.setName(name),
					validator: notBlank(),
				});

				if (showFiscalYearDropdown) {
					form.append(
						new FiscalYearPickerWidget({
							form,
							availableFiscalYears: my.availableFiscalYears,
							nextFiscalYear: my.nextFiscalYear,
							onChange: (fiscalYear, range) => {
								budget.setFiscalYear(fiscalYear);
								my.fiscalYearRange = range;
							},
						}),
					);
				}
			},
		});
	};

	my.submitFiscalYear = async function (budget) {
		const createdFiscalYear = await my.fiscalYearRepository.save({
			model: {
				start: my.fiscalYearRange.startDate,
				end: my.fiscalYearRange.endDate,
			},
			filter: {
				fiscalYearId: my.availableFiscalYears[1].getId(),
			},
		});

		const currentCompany = session.getCompany();
		const allYears = [...currentCompany.getYears(), createdFiscalYear];
		currentCompany.setYears(allYears);
		budget.setFiscalYear(createdFiscalYear);
	};

	my.submitBudget = async function (budget) {
		const savedBudget = await my.budgetRepository.save({
			model: budget,
		});

		savedBudget.getFiscalYear().addBudget(savedBudget);
		my.redirectToBudgetEditor(savedBudget);
	};

	my.saveBudget = async function (budget) {
		const budgetFiscalYear = budget.getFiscalYear();
		const isAboutNextFiscalYear = budgetFiscalYear === my.nextFiscalYear;

		if (isAboutNextFiscalYear) {
			await my.submitFiscalYear(budget);
		}

		return my.submitBudget(budget);
	};

	my.deleteBudget = async function (budget) {
		await my.budgetRepository.remove({
			model: budget,
			filter: {
				budgetId: budget.getId(),
			},
		});

		budget.getFiscalYear().removeBudget(budget);
		my.budgetListWidget.unselectBudget();
		that.show();
	};

	my.updateBudget = async function (budget) {
		await my.budgetRepository.update({
			model: budget,
			filter: {
				budgetId: budget.getId(),
			},
		});

		that.show();
	};

	my.setBudgetAsCurrent = async function (budget) {
		budget.setCurrent(true);

		await my.budgetRepository.update({
			model: budget,
			filter: { budgetId: budget.getId() },
		});

		my.reloadPage();
	};

	my.createBudgetFromFile = async function (file) {
		const uploadedFiscalYear = await my.budgetImporter.saveBudgetInfo();
		if (uploadedFiscalYear) {
			my.showImportBudgetDialog({
				file,
				uploadedFiscalYear,
			});
		}
	};

	my.redirectToBudgetEditor = function (budget) {
		my.redirectTo(`budget-editor/${budget.getId()}`);
	};

	my.getSelectedBudget = function () {
		return my.budgetListWidget.getSelectedBudget();
	};

	my.isEmpty = function () {
		return my.budgetListWidget.getBudgets().length === 0;
	};

	my.reloadPage = function () {
		router.getRouter().reloadPage();
	};

	my.onUploadError = function ({ error }) {
		notifier.error(error.message);
	};
});

export default budgetArchiveDocument;
